/* Supplier links + supplier-catalog browse helpers. */

import { api } from "./api";

export type SupplierLink = {
  id: number;
  seller_slug: string;
  seller_name: string;
  seller_company_type: string;
  seller_country: string;
  seller_lat: number | null;
  seller_lng: number | null;
  /** Owner of the counterparty company — the Contact column on both lists. */
  contact_email: string;
  contact_name: string;
  is_active: boolean;
  created_at: string;
  /** Recorded by hand — no account on the platform; its data is ours to edit. */
  is_placeholder?: boolean;
  /** The partner's public page opens for us («Кто видит страницу»). */
  page_open?: boolean;
  /** Requisites and contacts — on the partner-list read (with counts). */
  details?: PartnerDetails | null;
  /** How many of the supplier's products are open to us. */
  visible_product_count: number;
  /** What the seller's price tags mean — shown in the browse header. */
  seller_currency?: string;
  seller_prices_include_vat?: boolean;
  /** The seller's «Цены, по которым заказывают клиенты» block, resolved: what
   *  their catalogue is quoted in, and the rate it converts at (a rate they
   *  pinned, else the day's ECB figure). Null rate = the stored euro figure. */
  seller_price_currency?: string;
  seller_price_rate?: number | null;
  /** Our "отобразить на карте" flag; null until the partner is mirrored. */
  show_on_map: boolean | null;
  /** Two-stage break-up state (spec 2026-08-16). */
  termination_pending?: boolean;
  termination_requested_by_me?: boolean | null;
  termination_requested_at?: string | null;
  termination_comment?: string;
};

/** The same link row read from the seller's end — a company that buys from us. */
export type ClientLink = {
  id: number;
  buyer_slug: string;
  buyer_name: string;
  buyer_company_type: string;
  buyer_country: string;
  buyer_lat: number | null;
  buyer_lng: number | null;
  contact_email: string;
  contact_name: string;
  is_active: boolean;
  created_at: string;
  /** Recorded by hand — no account on the platform; its data is ours to edit. */
  is_placeholder?: boolean;
  /** The partner's public page opens for us («Кто видит страницу»). */
  page_open?: boolean;
  /** Requisites and contacts — on the partner-list read (with counts). */
  details?: PartnerDetails | null;
  /** How many of *our* products are open to this client. */
  visible_product_count: number;
  show_on_map: boolean | null;
  termination_pending?: boolean;
  termination_requested_by_me?: boolean | null;
  termination_requested_at?: string | null;
  termination_comment?: string;
  /** What we know about this client, behind the gear on «Мои клиенты»: does
   *  their freight cross a border, how many days they have to pay, and
   *  whatever the sales desk needs to remember. Standing answers — the order
   *  and the invoice start from them instead of asking every time. Seller-side
   *  only; the buyer's own «Мои поставщики» row never carries them. */
  client_is_export?: boolean;
  client_payment_days?: number | null;
  client_note?: string;
  /** «Выбирает партию»: the client may pick any lot for a line (2026-09-28). */
  client_lot_choice?: boolean;
  /** Who works with this client. Filled in by the work itself — whoever
   *  invited them, accepted them, wrote or moved their order — and by the
   *  owner by hand. Owners are never listed. */
  managers?: ClientManager[];
};

export type ClientManager = {
  member_id: number;
  user_id: number;
  full_name: string;
  source: "auto" | "manual";
};

/** A partial edit of the three columns above — send only what changed. */
export type ClientSettingsPatch = {
  client_is_export?: boolean;
  client_payment_days?: number | null;
  client_note?: string;
  client_lot_choice?: boolean;
};

export type SupplierProduct = {
  id: number;
  name: string;
  sku: string;
  ean: string;
  category_id: number | null;
  category_name: string | null;
  price_eur: number;
  vat_rate: number;
  unit: string;
  packaging_qty: number;
  /** «Штук в транспортной коробке» from the card — the real box size; the
   *  legacy packaging_qty is only a fallback when this is not filled in. */
  pcs_per_box?: number | null;
  min_order_qty: number;
  is_active: boolean;
  total_stock: number;
  primary_image_url: string | null;
  primary_thumb_url?: string | null;
  primary_tile_url?: string | null;
  /** The supplier's note to partners about this card («будет готова к 01.10»). */
  partner_note?: string;
  /** Our copy of a card the supplier no longer offers: listed greyed, not
   *  orderable (spec 20260915 §2.2). Only with `include_unavailable`. */
  supplier_gap_reason?: "" | "access_closed" | "delisted" | "link_ended";
  /** «Доступна к заказу» off on the seller's card: listed greyed, not orderable. */
  is_orderable?: boolean;
  /** «Статус наличия» — for registered clients composing an order. */
  availability_status?: "" | "in_stock" | "on_order" | "unavailable" | "expected";
  availability_expected_on?: string | null;
  /** Short-dated lots at their discount, where it beats this client's price. */
  expiry_offers?: ExpiryOffer[];
  /** Other lots to pick at the client's own price («Выбирает партию»). */
  lots?: PickableLot[];
};

/** A lot on the supplier's shelf the client may pick for a line (2026-09-28). */
export type PickableLot = {
  lot_id: number;
  batch_no: string;
  expires_on: string | null;
  days_left: number | null;
  available: number;
};

/** A lot the client may buy at its shelf-life discount (2026-09-28). */
export type ExpiryOffer = {
  lot_id: number;
  batch_no: string;
  expires_on: string | null;
  days_left: number | null;
  price_eur: number | string;
  discount_pct: number | string;
  /** Against the client's own price. */
  saving_pct: number;
  available: number;
  /** "expiry" (the ladder), "sale_expiry" / "sale_quantity" («Распродажа»). */
  kind?: string;
  sale_ends_on?: string | null;
};

export type PaginatedSupplierProducts = {
  count: number;
  results: SupplierProduct[];
};

/** Companies that sell to us. Severed links are left out unless asked for:
 *  unlinking deactivates the row rather than deleting it, and a deactivated
 *  row is not a partner. `withCounts` adds the available-products column at
 *  one COUNT query per row — only the partners page needs it. */
export const fetchSuppliers = (
  slug: string,
  activeOnly = true,
  withCounts = false,
): Promise<SupplierLink[]> =>
  api.get<SupplierLink[]>(
    `/t/${slug}/suppliers?active_only=${activeOnly}&with_counts=${withCounts}`,
  );

/** Every supplier a card can be tagged with, by name (owner, 2026-09-22):
 *  «Мои поставщики» — the partner links, those recorded by hand included —
 *  plus the supplier directory where the company keeps one. A manufacturer
 *  has no directory, so its links are the whole list. Unique regardless of
 *  case, sorted; either source failing just leaves it out. */
export async function fetchSupplierNames(slug: string): Promise<string[]> {
  const [records, links] = await Promise.all([
    api.get<{ id: number; name: string }[]>(`/t/${slug}/supplier-directory/names`).catch(() => []),
    fetchSuppliers(slug).catch(() => [] as SupplierLink[]),
  ]);
  const byKey = new Map<string, string>();
  for (const n of [...records.map((r) => r.name), ...links.map((l) => l.seller_name)]) {
    const name = (n ?? "").trim();
    if (name && !byKey.has(name.toLowerCase())) byKey.set(name.toLowerCase(), name);
  }
  return [...byKey.values()].sort((a, b) => a.localeCompare(b));
}

export const addSupplier = (slug: string, sellerSlug: string): Promise<SupplierLink> =>
  api.post<SupplierLink>(`/t/${slug}/suppliers`, { seller_slug: sellerSlug });

export const removeSupplier = (slug: string, sellerSlug: string): Promise<void> =>
  api.delete(`/t/${slug}/suppliers/${sellerSlug}`);

/** The same rows read from the seller's end — see {@link fetchSuppliers}. */
export const fetchClients = (
  slug: string,
  activeOnly = true,
  withCounts = false,
): Promise<ClientLink[]> =>
  api.get<ClientLink[]>(`/t/${slug}/clients?active_only=${activeOnly}&with_counts=${withCounts}`);

export const removeClient = (slug: string, buyerSlug: string): Promise<void> =>
  api.delete(`/t/${slug}/clients/${buyerSlug}`);

/** Save one or more of the three client columns. PATCH, so the export tick,
 *  the payment days and the comment each save on their own — three controls on
 *  one row, edited one at a time. */
export const updateClientSettings = (
  slug: string,
  buyerSlug: string,
  patch: ClientSettingsPatch,
): Promise<ClientLink> =>
  api.patch<ClientLink>(`/t/${slug}/clients/${buyerSlug}/settings`, patch);

/** Attach a colleague to a client by hand (owner / team.edit). The automatic
 *  path writes the same row; this is for handing a client to somebody before
 *  they have touched it. Both return the refreshed client row. */
export const addClientManager = (slug: string, buyerSlug: string, memberId: number): Promise<ClientLink> =>
  api.put<ClientLink>(`/t/${slug}/clients/${buyerSlug}/managers/${memberId}`);
export const removeClientManager = (slug: string, buyerSlug: string, memberId: number): Promise<ClientLink> =>
  api.delete<ClientLink>(`/t/${slug}/clients/${buyerSlug}/managers/${memberId}`);

/* ── Two-stage link termination (spec 2026-08-16) ──────────────────────────
 * Stage 1 files the break-up (with a comment; buyers also answer «сохранить
 * ассортимент?»); the requester may cancel; the other side confirms, which
 * deactivates the link and cleans up the shared data. */

export const terminateSupplierLink = (
  slug: string,
  sellerSlug: string,
  data: { comment?: string; keep_assortment?: boolean | null } = {},
): Promise<SupplierLink> =>
  api.post<SupplierLink>(`/t/${slug}/suppliers/${sellerSlug}/terminate`, data);

export const cancelSupplierTermination = (slug: string, sellerSlug: string): Promise<SupplierLink> =>
  api.post<SupplierLink>(`/t/${slug}/suppliers/${sellerSlug}/terminate/cancel`, {});

export const confirmSupplierTermination = (
  slug: string,
  sellerSlug: string,
  data: { keep_assortment?: boolean | null } = {},
): Promise<SupplierLink> =>
  api.post<SupplierLink>(`/t/${slug}/suppliers/${sellerSlug}/terminate/confirm`, data);

export const terminateClientLink = (
  slug: string,
  buyerSlug: string,
  data: { comment?: string; keep_assortment?: boolean | null } = {},
): Promise<ClientLink> =>
  api.post<ClientLink>(`/t/${slug}/clients/${buyerSlug}/terminate`, data);

export const cancelClientTermination = (slug: string, buyerSlug: string): Promise<ClientLink> =>
  api.post<ClientLink>(`/t/${slug}/clients/${buyerSlug}/terminate/cancel`, {});

export const confirmClientTermination = (
  slug: string,
  buyerSlug: string,
  data: { keep_assortment?: boolean | null } = {},
): Promise<ClientLink> =>
  api.post<ClientLink>(`/t/${slug}/clients/${buyerSlug}/terminate/confirm`, data);

/** The supplier's category tree behind what this buyer may browse (flat,
 *  with parent_id) — feeds the category filter on browse/order screens. */
export type SupplierCategory = { id: number; name: string; parent_id: number | null };
export const fetchSupplierCategories = (
  slug: string,
  sellerSlug: string,
): Promise<SupplierCategory[]> =>
  api.get<SupplierCategory[]>(`/t/${slug}/suppliers/${sellerSlug}/categories`);

/** One dropdown on the catalogue's filter bar, and the options it has.
 *  Derived from the products in view, never configured: a wine producer's
 *  catalogue offers Colour and Grape because its cards answer them, and a
 *  bakery's offers nothing rather than a row of empty selects. */
export type FacetValue = { code: string; name: string; count: number };
export type Facet = { code: string; name: string; values: FacetValue[] };

export const fetchSupplierFacets = (
  slug: string,
  sellerSlug: string,
  categoryId?: number | null,
): Promise<Facet[]> =>
  api.get<Facet[]>(
    `/t/${slug}/suppliers/${sellerSlug}/facets` +
      (categoryId != null ? `?category_id=${categoryId}` : ""),
  );

/** «colour:red,style:dry» — values of one characteristic are alternatives,
 *  different characteristics are conditions. The server reads it the same way. */
export const encodeFacets = (selected: Record<string, string>): string =>
  Object.entries(selected)
    .filter(([, v]) => v)
    .map(([code, value]) => `${code}:${value}`)
    .join(",");

export const browseSupplierProducts = (
  slug: string,
  sellerSlug: string,
  params: {
    page?: number;
    q?: string;
    category_id?: number;
    facets?: string;
    include_unavailable?: boolean;
  } = {},
): Promise<PaginatedSupplierProducts> => {
  const qs = new URLSearchParams();
  if (params.page) qs.set("page", String(params.page));
  if (params.include_unavailable) qs.set("include_unavailable", "true");
  if (params.q) qs.set("q", params.q);
  if (params.category_id) qs.set("category_id", String(params.category_id));
  if (params.facets) qs.set("facets", params.facets);
  const suffix = qs.toString() ? "?" + qs : "";
  return api.get<PaginatedSupplierProducts>(
    `/t/${slug}/suppliers/${sellerSlug}/products${suffix}`,
  );
};

/** What «Мои партнёры» can show about a partner beyond its name (2026-09-22):
 *  its requisites as our books have them, and — recorded by hand — the
 *  contact and accountant written down with it. */
export type PartnerDetails = {
  registration_number: string;
  vat_number: string;
  legal_address: string;
  physical_address: string;
  phone: string;
  email: string;
  bank: string;
  licences: string;
  contact_position: string;
  contact_phone: string;
  contact_comment: string;
  accountant: string;
};
